import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Suspense, useContext, useEffect, useMemo } from 'react';
import { Link, Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { SideNav, Spinner, useLocalizeMessage, Text, Icon } from 'libs.nucleus';
import { NotFoundPage, useAppBanner, useBooleanState, AclScope } from 'libs.react';
import { AuthContext, useScopeValidator, useUserRoles } from 'libs.react_internal';
import { StudyConfigurationContext } from '../../contexts/study_configuration/study_configuration.context';
import { useNavigation } from '../../hooks/use_navigation/use_navigation.hook';
import { StudyUpgradeModal } from '../study_upgrade_modal/study_upgrade_modal.component';
export const StudyWrapper = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { studyId } = useParams();
    const { entityId } = useContext(AuthContext);
    const { navItems } = useNavigation();
    const match = useMatch('/studies/:studyId/:section/*');
    const { showAppBanner, clearAppBanner } = useAppBanner();
    const translate = useLocalizeMessage();
    const { currentStudy, isInitialized, isLoading, isUpgradeAvailable } = useContext(StudyConfigurationContext);
    const [isUpgradeStudyModalOpen, openUpgradeStudyModal, closeUpgradeStudyModal] = useBooleanState();
    const { isDataUser, isUserAndSiteManager, isAdmin, isSiteUser } = useUserRoles();
    // Permission checks for determining default redirect
    const resource = `entity/${entityId}/study/${studyId}`;
    const canManageStudy = useScopeValidator(AclScope.MOM_STUDY_UPDATE, { resource });
    const canGetStudyEnvironments = useScopeValidator(AclScope.MOM_STUDY_LIST, { resource });
    const canManageSites = useScopeValidator(AclScope.MOM_ENVIRONMENT_LIST, { resource });
    useEffect(() => {
        validateRoute();
        if (isUpgradeAvailable) {
            showAppBanner({
                variant: 'warning',
                content: translate('Study configuration is locked. Please accept the required update to continue editing.'),
                actionButton: {
                    label: translate('Learn more'),
                    content: (_jsxs("div", { className: 'flex items-center gap-1 text-text-primary', children: [_jsx(Text, { size: 'xs', children: translate('Learn more') }), _jsx(Icon, { name: 'arrowForward', color: 'current', size: 16 })] })),
                    onClick: openUpgradeStudyModal,
                },
            });
        }
        return () => {
            clearAppBanner();
        };
    }, [
        currentStudy,
        isInitialized,
        isUpgradeAvailable,
        canManageStudy,
        isDataUser,
        isUserAndSiteManager,
        isAdmin,
        canManageSites,
        canGetStudyEnvironments,
        match,
    ]);
    /**
     * Validates the current study route
     * - If the study is initialized but there is no route match (meaning we're at /studies/{studyId} without a section)
     * then redirect to the appropriate page based on user permissions
     */
    const validateRoute = () => {
        if (!isInitialized && !match) {
            // Determine the appropriate default section based on user permissions
            if (canManageStudy) {
                // Users who can manage study should go to build
                navigate('build');
            }
            else if (isDataUser) {
                // Data roles should go to data section
                navigate('data/reports');
            }
            else if (isSiteUser) {
                // Site users should go to dashboard
                navigate('/site');
            }
            else if (canManageSites || canGetStudyEnvironments) {
                // Users who can manage sites/environments should go to tests
                navigate('tests');
            }
            else {
                // Fallback - redirect to build (will show 404 if no access)
                navigate('build');
            }
        }
    };
    const studyCardProps = useMemo(() => {
        return { protocolNumber: currentStudy.protocolNumber, studyName: currentStudy.name };
    }, [currentStudy.name, currentStudy.protocolNumber]);
    return (_jsxs("div", { className: 'flex grow overflow-hidden', children: [_jsx("div", { className: 'flex flex-col bg-neutral-75', "data-testid": 'side-nav-menu', children: currentStudy.name !== '' && (_jsx(SideNav, { activeLinkPath: pathname, items: navItems, LinkComponent: Link, studyCard: studyCardProps })) }), _jsxs("div", { className: 'flex h-full w-full grow flex-col overflow-hidden bg-white', children: [isLoading && _jsx(Spinner, { wrapper: 'full' }), !currentStudy.name && isInitialized && _jsx(NotFoundPage, { goBack: () => navigate(-1) }), currentStudy.name && (_jsx(Suspense, { fallback: _jsx("section", { className: 'flex items-center justify-center h-full', children: _jsx(Spinner, {}) }), children: _jsx(Outlet, {}) }))] }), isUpgradeStudyModalOpen && _jsx(StudyUpgradeModal, { onModalClose: closeUpgradeStudyModal })] }));
};
